import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon
} from "@chakra-ui/react";
import { FaSun, FaTint, FaArrowRight, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterFiltrationUV: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-filtration/uv-system"
        fallbackTitle="UV Water Filtration System — One Water Australia"
        fallbackDescription="Understand what UV water filtration does, when you need it, and how it fits into a complete water treatment system for Australian homes."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1581093577421-f561a654a353?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaSun} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              UV water filtration: what it does and when you need it.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              Ultraviolet disinfection targets what carbon filters can't — biological contaminants.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* What UV filtration does */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Heading size="xl" color={NAVY}>What UV Filtration Actually Does</Heading>
            <Text color="gray.600" fontSize="lg">
              UV filtration exposes water to ultraviolet light at a specific wavelength. This destroys the DNA of bacteria, viruses, and parasites — rendering them unable to reproduce and therefore harmless.
            </Text>
            <Text color="gray.600">
              It doesn't add chemicals to your water. It doesn't change the taste or smell. It doesn't remove anything physical from the water — it purely provides biological disinfection.
            </Text>
            <Box p={5} bg={ICE_MIST} borderRadius="xl">
              <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">Important: What UV does NOT do</Text>
              <VStack align="flex-start" spacing={2}>
                {[
                  "Does not remove chemicals, dissolved solids, or heavy metals",
                  "Does not remove sediment, chlorine, or fluoride",
                  "Does not replace carbon filtration",
                  "Is not effective if water is turbid — pre-filtration is required",
                ].map((item) => (
                  <HStack key={item} spacing={2} align="flex-start">
                    <Icon as={FaExclamationTriangle} color={DEEP_AQUA} boxSize={3} mt={0.5} flexShrink={0} />
                    <Text fontSize="sm" color="gray.700">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
          <SimpleGrid columns={2} spacing={4}>
            {[
              { label: "Destroys bacteria", yes: true },
              { label: "Destroys viruses", yes: true },
              { label: "Destroys parasites (Giardia, Cryptosporidium)", yes: true },
              { label: "Chemical-free disinfection", yes: true },
              { label: "Removes dissolved solids", yes: false },
              { label: "Removes chlorine", yes: false },
              { label: "Removes sediment", yes: false },
              { label: "Changes water taste", yes: false },
            ].map((item) => (
              <Box
                key={item.label}
                p={4}
                borderRadius="lg"
                bg={item.yes ? ICE_MIST : "gray.50"}
                border="1px solid"
                borderColor={item.yes ? `${WATER_BLUE}40` : "gray.200"}
              >
                <HStack spacing={2} align="flex-start">
                  <Text color={item.yes ? DEEP_AQUA : "gray.400"} fontSize="sm" flexShrink={0} mt={0.5}>
                    {item.yes ? "✓" : "✕"}
                  </Text>
                  <Text fontSize="xs" color={item.yes ? "gray.700" : "gray.500"}>{item.label}</Text>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        </SimpleGrid>
      </Container>

      {/* When UV makes sense */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>When UV Makes Sense</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Not everyone needs UV. By saying so directly, we earn trust from the people who do.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {[
              { title: "Tank or rainwater", desc: "Rainwater tanks can harbour bacteria and biological contamination. UV is standard specification for tank water systems." },
              { title: "Bore water", desc: "Bores can introduce biological contaminants that aren't present in mains-treated supplies. UV provides the protection that treatment plants would otherwise provide." },
              { title: "Rural properties", desc: "Properties on rural or agricultural land may be subject to elevated biological contamination risk in water supplies." },
              { title: "Chemical-free preference", desc: "Some households want biological protection without ongoing chemical addition. UV achieves this without adding anything to the water." },
              { title: "Ageing infrastructure", desc: "Very old distribution systems can introduce biological risk during the 'last mile' before water reaches your tap." },
              { title: "Post-flood or high-risk events", desc: "Following flooding or supply contamination events, UV provides an additional protection layer." },
            ].map((item) => (
              <Box key={item.title} p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={5} mb={3} />
                <Heading size="sm" color={NAVY} mb={2}>{item.title}</Heading>
                <Text fontSize="sm" color="gray.600">{item.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* How we integrate UV */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <Box>
            <Heading size="xl" color={NAVY} mb={6}>How One Water Integrates UV</Heading>
            <Text color="gray.600" mb={6} fontSize="lg">
              UV is one stage in a whole-home or point-of-entry system — not a standalone solution. Pre-filtration is essential: UV only works effectively on clear water.
            </Text>
            <Text color="gray.600" mb={6}>
              A properly specified UV system sits downstream of sediment and carbon filtration, where it receives clear, pre-treated water. This protects the UV lamp and ensures the biological disinfection is effective.
            </Text>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`}>
              <Text fontWeight="600" color={NAVY} mb={3} fontSize="sm">Typical UV system sequence:</Text>
              <VStack align="flex-start" spacing={2}>
                {[
                  "Sediment pre-filter → Activated carbon → UV disinfection → (optional post-polish)",
                ].map((step) => (
                  <Text key={step} fontSize="sm" color="gray.700">{step}</Text>
                ))}
              </VStack>
            </Box>
          </Box>
          <VStack spacing={5}>
            <Box borderRadius="xl" overflow="hidden" h="220px" w="full" shadow="md">
              <Box
                as="img"
                src="https://images.unsplash.com/photo-1581093577421-f561a654a353?auto=format&fit=crop&w=800&q=80"
                alt="UV water purification system — ultraviolet disinfection technology"
                w="full"
                h="full"
                objectFit="cover"
              />
            </Box>
            <Box p={8} bg={ICE_MIST} borderRadius="2xl" w="full">
              <VStack spacing={5} textAlign="center">
                <Icon as={FaSun} boxSize={12} color={DEEP_AQUA} />
                <Heading size="lg" color={NAVY}>99.99% pathogen effectiveness</Heading>
                <Text color="gray.600" fontSize="sm">
                  When properly specified and maintained, UV filtration achieves greater than 99.99% reduction in bacteria, viruses, and cysts — without chemicals, without taste impact, and without adding anything to your water.
                </Text>
                <VStack spacing={3} align="stretch" w="full">
                  {["No chemicals added", "No taste or odour impact", "Low maintenance requirements", "Effective against chlorine-resistant parasites"].map((b) => (
                    <HStack key={b} spacing={2}>
                      <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} />
                      <Text fontSize="sm" color="gray.700">{b}</Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl" textAlign="center">
          <VStack spacing={5}>
            <Heading size="xl" color="white">We only recommend UV when it's appropriate.</Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="xl">
              If your water supply doesn't warrant UV, we'll say so. Our assessment covers your specific water source — and your system recommendation reflects that.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Related */}
      <Box bg="gray.50" py={12}>
        <Container maxW="container.xl">
          <Heading size="md" color={NAVY} mb={6}>Related Reading</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {[
              { title: "Water Filtration Systems", href: "/water/water-filtration", desc: "The full picture of filtration technology options." },
              { title: "Water Purifiers", href: "/water/water-purifiers", desc: "When filtration levels require purification-grade systems." },
              { title: "Reverse Osmosis Guide", href: "/water/blog/reverse-osmosis-guide", desc: "The other technology for high-demand treatment." },
            ].map((link) => (
              <Box
                key={link.title}
                as={RouterLink}
                to={link.href}
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: WATER_BLUE }}
                transition="all 0.2s"
                display="block"
                textDecoration="none"
              >
                <Text fontWeight="600" color={NAVY} mb={1} fontSize="sm">{link.title}</Text>
                <Text fontSize="sm" color="gray.600">{link.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterFiltrationUV;
